import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from '@material-ui/core'
import { CircleX, Tag } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { toast } from 'react-toastify'
import { useTags } from '../../context/Tickets/TagsContext'
import { ButtonSecondary } from '../ButtonSecondary'
import { getTextColor, getColorConfig } from '../TagModal/color'
import { useStyles } from './style'

export const ContactTags = ({
  contact,
  ticket: propsTicket,
  filterTags = [],
  showAllTypes = false,
  disabledAddTags = false
}) => {
  const classes = useStyles()
  const [openDialog, setOpenDialog] = useState(false)
  const [processingTag, setProcessingTag] = useState(null)
  const [ticket, setTicket] = useState(propsTicket || {})

  const {
    getContactTags,
    getTicketTags,
    getAllTags,
    addTag,
    removeTag,
    fetchAndSetTags,
    fetchAllTags
  } = useTags()

  useEffect(() => {
    setTicket(propsTicket)
  }, [propsTicket])

  useEffect(() => {
    if (contact?.id) {
      fetchAndSetTags('contact', contact.id)
    }
    if (ticket?.id) {
      fetchAndSetTags('ticket', ticket.id)
    }
  }, [contact?.id, ticket?.id, fetchAndSetTags])

  const contactTags = getContactTags(contact?.id) || []
  const ticketTags = getTicketTags(ticket?.id) || []

  const combinedTags = useMemo(() => {
    const contactOnlyTags = contactTags.filter(tag => tag.tipo === 'contato')
    const ticketOnlyTags = ticketTags.filter(tag => tag.tipo === 'atendimento')
    return [...contactOnlyTags, ...ticketOnlyTags]
  }, [contactTags, ticketTags])

  const allTags = getAllTags()

  const availableTags = useMemo(() => {
    if (!allTags.length) return []
    if (showAllTypes) return allTags
    return filterTags.length > 0
      ? allTags.filter(tag => filterTags.includes(tag.tipo))
      : allTags
  }, [allTags, showAllTypes, filterTags])

  const displayTags = useMemo(() => {
    if (showAllTypes) return combinedTags
    return filterTags.length === 0
      ? combinedTags
      : combinedTags.filter(tag => filterTags.includes(tag.tipo))
  }, [combinedTags, filterTags, showAllTypes])

  const isTagApplied = useCallback(
    tagId => {
      return combinedTags.some(tag => String(tag.id) === String(tagId))
    },
    [combinedTags]
  )

  const canModifyTags = useCallback(() => {
    if (!ticket?.status) return true
    if (ticket.status !== 'open') {
      toast.error('Não é permitido modificar tags em tickets pendentes.')
      return false
    }
    return true
  }, [ticket?.status])

  const handleOpenDialog = useCallback(async () => {
    if (!canModifyTags()) return
    await fetchAllTags()
    setOpenDialog(true)
  }, [canModifyTags, fetchAllTags])

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false)
  }, [])

  const handleTagToggle = useCallback(
    async tag => {
      if (processingTag === tag.id) return

      const isApplied = isTagApplied(tag.id)
      setProcessingTag(tag.id)

      const type = tag.tipo === 'contato' ? 'contact' : 'ticket'
      const id = type === 'contact' ? contact?.id : ticket?.id

      try {
        if (isApplied) {
          await removeTag(type, id, tag.id)
        } else {
          await addTag(type, id, tag.id)
        }
      } catch (error) {
        console.error('Erro no toggle da tag:', error)
        toast.error('Erro inesperado ao processar tag')
      } finally {
        setProcessingTag(null)
      }
    },
    [processingTag, isTagApplied, addTag, removeTag, contact?.id, ticket?.id]
  )

  const getTagStyles = tag => {
    const config = getColorConfig(tag.color)
    return {
      backgroundColor: config.value,
      color: config.textColor,
      border: `1px solid ${config.border}`
    }
  }

  return (
    <div className={classes.tagsContainer}>
      {!disabledAddTags && (
        <Tooltip title="Gerenciar Tags">
          <IconButton onClick={handleOpenDialog} className={classes.tagButton}>
            <Tag />
          </IconButton>
        </Tooltip>
      )}

      {!disabledAddTags && (
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="xs"
          fullWidth
          className={classes.dialogTags}
        >
          <DialogTitle className={classes.dialogTitle}>
            Gerenciar Tags
          </DialogTitle>
          <DialogContent dividers>
            <Box>
              {['atendimento', 'contato'].map(category => (
                <Box key={category} className={classes.boxTagCategory}>
                  <Typography
                    variant="span"
                    gutterBottom
                    className={classes.title}
                  >
                    {category === 'atendimento' ? 'Atendimento' : 'Contato'}
                  </Typography>
                  <Grid container spacing={1}>
                    {availableTags
                      .filter(tag => tag.tipo === category)
                      .map(tag => (
                        <Grid item key={tag.id}>
                          <Chip
                            label={tag.name}
                            style={{
                              ...(isTagApplied(tag.id)
                                ? getTagStyles(tag)
                                : {
                                    backgroundColor: 'transparent',
                                    color: '#7B7B7B',
                                    border: '1px solid #7B7B7B'
                                  }),
                              cursor: 'pointer',
                              opacity: processingTag === tag.id ? 0.6 : 1
                            }}
                            className={classes.tagBadgeItem}
                            onClick={() => handleTagToggle(tag)}
                            disabled={processingTag === tag.id}
                          />
                        </Grid>
                      ))}
                    {!availableTags.filter(tag => tag.tipo === category)
                      .length && (
                      <Grid item>
                        <Typography variant="body2" color="textSecondary">
                          Nenhuma tag disponível
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <ButtonSecondary onClick={handleCloseDialog} label={'Fechar'} />
          </DialogActions>
        </Dialog>
      )}
      <div className={classes.tags}>
        {displayTags?.length > 0 ? (
          <div className={classes.tagsList}>
            {displayTags.map(tag => (
              <Chip
                key={tag.id}
                size="small"
                label={tag.name}
                style={getTagStyles(tag)}
                className={classes.tagBadgeItem}
                onDelete={
                  !disabledAddTags ? () => handleTagToggle(tag) : undefined
                }
                deleteIcon={
                  !disabledAddTags ? (
                    <CircleX style={{ color: getTextColor(tag.color) }} />
                  ) : undefined
                }
              />
            ))}
          </div>
        ) : (
          !disabledAddTags && (
            <span className={classes.emptyTags}>Nenhuma Tag Relacionada</span>
          )
        )}
      </div>
    </div>
  )
}

export default ContactTags
